import React, { useEffect, useState } from 'react';
import api from '../utils/api';

/**
 * Read-only status panel for the Monthly Outstanding-Bills Statement
 * scheduler. Admin sees: next run date, eligibility count, last run summary.
 * Mirrors how the rest of the platform exposes background-task health.
 */
export default function MonthlyStatementStatus() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [s, r] = await Promise.all([
        api.get('/admin/monthly-statements/status'),
        api.get('/admin/monthly-statements/runs?limit=6'),
      ]);
      setStatus(s.data);
      setRuns(r.data?.runs || []);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      });
    } catch { return iso; }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200" data-testid="monthly-statement-status">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">📬 Monthly Outstanding-Bills Statement</h3>
          <p className="text-xs text-gray-500 mt-1">
            Sent automatically on the 1st of every month (02:00 UTC) to customers with any unpaid invoice.
            The statement lists every outstanding bill (regardless of age) as of the end of the previous month.
            Uses the SMTP settings above. No manual configuration needed.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
          data-testid="monthly-statement-refresh"
        >
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 mb-3">{err}</div>
      )}

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">Next scheduled run</div>
            <div className="text-sm font-bold text-blue-900 mt-1" data-testid="monthly-statement-next-run">
              {fmtDate(status.next_run_at)}
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Customers eligible right now</div>
            <div className="text-sm font-bold text-amber-900 mt-1" data-testid="monthly-statement-eligible">
              {status.eligible_customer_count} customer{status.eligible_customer_count === 1 ? '' : 's'}
            </div>
            <div className="text-[10px] text-amber-700 mt-0.5">have email + outstanding balance</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-600 font-semibold">Configuration</div>
            <div className="text-xs text-gray-800 mt-1 leading-snug">
              Mode: <b>Pending bills only</b><br />
              Scope: <b>All unpaid invoices (any age)</b><br />
              Cutoff: <b>End of previous month</b>
            </div>
          </div>
        </div>
      )}

      {/* Last run summary */}
      {status?.last_run ? (
        <div className="mt-3 border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 flex items-center justify-between">
            <span>Last run · {status.last_run.year_month}</span>
            <span className="text-gray-500">{fmtDate(status.last_run.finished_at || status.last_run.started_at)}</span>
          </div>
          <div className="px-3 py-2 text-xs grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Scanned" value={status.last_run.totals?.customers_scanned} color="gray" />
            <Stat label="Sent" value={status.last_run.totals?.sent} color="green" />
            <Stat label="No balance" value={status.last_run.totals?.skipped_no_balance} color="gray" />
            <Stat label="No email" value={status.last_run.totals?.skipped_no_email} color="gray" />
            <Stat label="Failed" value={status.last_run.totals?.failed} color="red" />
          </div>
        </div>
      ) : (
        <div className="mt-3 text-xs text-gray-500 italic">
          No runs yet. The scheduler will dispatch on the 1st of the next month.
        </div>
      )}

      {/* Recent runs (compact list) */}
      {runs.length > 1 && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-blue-700 hover:underline">Show {runs.length - 1} earlier run(s)</summary>
          <ul className="mt-1 space-y-1">
            {runs.slice(1).map((r) => (
              <li key={r.id} className="border-l-2 border-gray-200 pl-2 text-gray-600">
                <b>{r.year_month}</b> · {fmtDate(r.started_at)} · sent {r.totals?.sent || 0} / failed {r.totals?.failed || 0}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    gray: 'text-gray-800',
    green: 'text-emerald-700',
    red: 'text-red-700',
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-sm font-bold ${colors[color] || colors.gray}`}>{value ?? 0}</div>
    </div>
  );
}
